import { createStyles, Tooltip } from "@mantine/core";
import React, { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import swal from "sweetalert";
import moment from "moment-timezone";
import { updateProfile, getProfile } from "../../services/profile";
import { syncStediClaimStatus } from "../../services/billing";
import CreateClaimDrawer from "./CreateClaimDrawer";
import VerifyEligibilityModal from "./VerifyEligibilityModal";
import UsePreviousClaimDrawer from "./UsePreviousClaimDrawer";
import ClaimTypeChooser from "./ClaimTypeChooser";
import { LuPlus, LuFileText, LuRefreshCw } from "react-icons/lu";
import { IoSettingsOutline } from "react-icons/io5";

const useStyles = createStyles(() => ({
  toolbar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: "12px",
    marginBottom: "20px",
    padding: "14px 20px",
    background: "var(--app-surface)",
    border: "1px solid var(--app-border)",
    borderRadius: "12px",
    boxShadow: "0 1px 2px rgba(16, 24, 40, 0.04)",
  },

  toolbarLeft: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    flexWrap: "wrap",
  },

  ctaBtn: {
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    background: "var(--app-primary)",
    color: "var(--app-on-primary)",
    fontWeight: 600,
    fontSize: "14px",
    fontFamily: "Inter, sans-serif",
    border: "none",
    borderRadius: "8px",
    padding: "9px 16px",
    cursor: "pointer",
    "&:hover": { background: "var(--app-primary)" },
    "& svg": { width: "16px", height: "16px", flexShrink: 0, display: "block" },
  },

  toolbarRight: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },

  checkStatusBtn: {
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    // background: "transparent",
        background: "#FFAF4A",
            color: "#171717",
    color: "#374151",
    fontWeight: 600,
    fontSize: "13px",
    fontFamily: "Inter, sans-serif",
    border: "1px solid #E5E5E5",
    borderRadius: "8px",
    padding: "8px 14px",
    cursor: "pointer",
    "&:hover": { background: "#F0A03E" },
    "&:disabled": { opacity: 0.6, cursor: "not-allowed" },
    "& svg": { width: "15px", height: "15px", flexShrink: 0, display: "block" },
  },

  checkStatusSpin: {
    animation: "checkStatusSpin 0.8s linear infinite",
    "@keyframes checkStatusSpin": {
      from: { transform: "rotate(0deg)" },
      to: { transform: "rotate(360deg)" },
    },
  },

  settingsIconBtn: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    background: "transparent",
    color: "var(--app-text-muted)",
    border: "none",
    borderRadius: "8px",
    padding: "7px",
    cursor: "pointer",
    "&:hover": { background: "var(--app-border)", color: "var(--app-text)" },
    "& svg": { width: "20px", height: "20px", flexShrink: 0, display: "block" },
  },

  startClaimWrap: {
    position: "relative",
    display: "inline-block",
  },

  startClaimMenu: {
    position: "absolute",
    top: "calc(100% + 6px)",
    left: 0,
    background: "var(--app-surface)",
    border: "1px solid var(--app-border)",
    borderRadius: "12px",
    boxShadow: "0 16px 40px rgba(15, 23, 42, 0.14)",
    padding: "6px",
    minWidth: "280px",
    zIndex: 200,
    display: "flex",
    flexDirection: "column",
    gap: "2px",
  },

  startClaimMenuItem: {
    display: "flex",
    alignItems: "flex-start",
    gap: "10px",
    padding: "10px 10px",
    borderRadius: "8px",
    cursor: "pointer",
    border: "none",
    background: "transparent",
    width: "100%",
    textAlign: "left",
    "&:hover": { background: "var(--app-surface-2)" },
  },

  startClaimMenuIco: {
    width: "34px",
    height: "34px",
    borderRadius: "8px",
    background: "var(--app-tint-amber)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    marginTop: "1px",
    "& svg": { width: "16px", height: "16px", color: "#B7791F", display: "block" },
  },

  startClaimMenuInfo: {
    display: "flex",
    flexDirection: "column",
  },

  startClaimMenuTitle: {
    fontFamily: "Inter, sans-serif",
    fontSize: "13.5px",
    fontWeight: 700,
    color: "var(--app-text)",
    lineHeight: "1.3",
    margin: "0 0 2px",
    display: "block",
  },

  startClaimMenuSub: {
    fontFamily: "Inter, sans-serif",
    fontSize: "12px",
    fontWeight: 400,
    color: "var(--app-text-muted)",
    lineHeight: "1.4",
    margin: 0,
    display: "block",
  },
}));

const CHECK_STATUS_COOLDOWN_HOURS = 3;

function ClaimToolbar() {
  const { classes } = useStyles();
  const navigate = useNavigate();
  const { profile } = useSelector((state) => state?.profile);
  const { SubscriptionCard } = useSelector((state) => state?.auth);
  const { billingcard } = useSelector((state) => state?.billing);

  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [chooserOpen, setChooserOpen] = useState(false);
  const [claimDrawerOpen, setClaimDrawerOpen] = useState(false);
  const [eligibilityModalOpen, setEligibilityModalOpen] = useState(false);
  const [prevClaimDrawerOpen, setPrevClaimDrawerOpen] = useState(false);
  const [prefillClaim, setPrefillClaim] = useState(null);
  const [checkingStatus, setCheckingStatus] = useState(false);
  const [isCheckStatusOnCooldown, setIsCheckStatusOnCooldown] = useState(false);
  const [cooldownLabel, setCooldownLabel] = useState("");
  const dropdownRef = useRef(null);
  const cooldownIntervalRef = useRef(null);

  // Drives the disabled/tooltip state from a "last used" UTC timestamp. Called
  // from the profile-driven effect below, and also fired optimistically right
  // after a successful sync so the button disables immediately instead of
  // waiting on the profile refetch to round-trip through redux.
  const applyCooldown = React.useCallback((lastStediUsed) => {
    if (cooldownIntervalRef.current) {
      clearInterval(cooldownIntervalRef.current);
      cooldownIntervalRef.current = null;
    }

    if (!lastStediUsed) {
      setIsCheckStatusOnCooldown(false);
      setCooldownLabel("");
      return;
    }

    const cooldownEndsAt = moment.utc(lastStediUsed).add(CHECK_STATUS_COOLDOWN_HOURS, "hours");

    const tick = () => {
      const msLeft = cooldownEndsAt.diff(moment());
      if (msLeft <= 0) {
        setIsCheckStatusOnCooldown(false);
        setCooldownLabel("");
        return false;
      }
      const totalMinutes = Math.ceil(msLeft / 60000);
      const hours = Math.floor(totalMinutes / 60);
      const minutes = totalMinutes % 60;
      setIsCheckStatusOnCooldown(true);
      setCooldownLabel(hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`);
      return true;
    };

    if (!tick()) return;
    cooldownIntervalRef.current = setInterval(() => {
      if (!tick()) {
        clearInterval(cooldownIntervalRef.current);
        cooldownIntervalRef.current = null;
      }
    }, 30000);
  }, []);

  // Landing on Billing already has `profile` from redux — derive the cooldown
  // state from it up front, and again whenever it's refreshed post-sync.
  React.useEffect(() => {
    applyCooldown(profile?.user?.laststediused);
  }, [profile, applyCooldown]);

  React.useEffect(() => {
    return () => {
      if (cooldownIntervalRef.current) clearInterval(cooldownIntervalRef.current);
    };
  }, []);

  React.useEffect(() => {
    const handleOutsideClick = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    };
    if (dropdownOpen) document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, [dropdownOpen]);

  const checkCard = async (action) => {
    if (!SubscriptionCard && billingcard?.BillingPayment === null) {
      await swal({
        title: "Card not Connected",
        text: `Please connect a bank or credit card account to ${action}.`,
        dangerMode: true,
      });
      navigate("/home/billing-settings", { state: { tab: "payment" } });
      return false;
    }
    return true;
  };

  const handleProfessionalClaimClick = async () => {
    // if (!(await checkCard("start a claim"))) return;
    setClaimDrawerOpen(true);
  };

  const handleCheckStatus = async () => {
    const adminId = profile?.user?.admin || profile?.user?.id;
    if (!adminId || checkingStatus || isCheckStatusOnCooldown) return;
    setCheckingStatus(true);
    try {
      const response = await syncStediClaimStatus(adminId);
      if (response) applyCooldown(moment.utc().toISOString());
      await getProfile();
    } finally {
      setCheckingStatus(false);
    }
  };

  return (
    <>
      <div className={classes.toolbar}>
        <div className={classes.toolbarLeft}>
          <div className={classes.startClaimWrap} ref={dropdownRef}>
            <button
              className={classes.ctaBtn}
              onClick={() => setDropdownOpen((prev) => !prev)}
            >
              <LuPlus />
              Start Claim
            </button>

            {dropdownOpen && (
              <div className={classes.startClaimMenu}>
                <button
                  className={classes.startClaimMenuItem}
                  onClick={() => { setDropdownOpen(false); setChooserOpen(true); }}
                >
                  <div className={classes.startClaimMenuIco}><LuPlus /></div>
                  <div className={classes.startClaimMenuInfo}>
                    <span className={classes.startClaimMenuTitle}>Start a New Claim</span>
                    <span className={classes.startClaimMenuSub}>Pick a claim type and fill it in from scratch.</span>
                  </div>
                </button>

                <button
                  className={classes.startClaimMenuItem}
                  onClick={() => { setDropdownOpen(false); setPrevClaimDrawerOpen(true); }}
                >
                  <div className={classes.startClaimMenuIco}><LuFileText /></div>
                  <div className={classes.startClaimMenuInfo}>
                    <span className={classes.startClaimMenuTitle}>Use a Previous Claim</span>
                    <span className={classes.startClaimMenuSub}>Duplicate a past claim and pre-fill its details.</span>
                  </div>
                </button>
              </div>
            )}
          </div>
        </div>

        <div className={classes.toolbarRight}>
          <Tooltip
            label={`Available again in ${cooldownLabel}`}
            disabled={!isCheckStatusOnCooldown}
            withArrow
            position="bottom"
          >
            <span style={{ display: "inline-block" }}>
              <button
                className={classes.checkStatusBtn}
                onClick={handleCheckStatus}
                disabled={checkingStatus || isCheckStatusOnCooldown}
              >
                <LuRefreshCw className={checkingStatus ? classes.checkStatusSpin : undefined} />
                {checkingStatus ? "Checking Status…" : "Check Status"}
              </button>
            </span>
          </Tooltip>

          <button
            className={classes.settingsIconBtn}
            onClick={() => navigate("/home/billing-settings")}
          >
            <IoSettingsOutline />
          </button>
        </div>
      </div>

      <ClaimTypeChooser
        open={chooserOpen}
        onClose={() => setChooserOpen(false)}
        onProfessional={handleProfessionalClaimClick}
        onInstitutional={() => {}}
      />

      <CreateClaimDrawer
        opened={claimDrawerOpen}
        onClose={() => { setClaimDrawerOpen(false); setPrefillClaim(null); }}
        prefillClaim={prefillClaim}
      />

      <VerifyEligibilityModal
        opened={eligibilityModalOpen}
        onClose={() => setEligibilityModalOpen(false)}
      />

      <UsePreviousClaimDrawer
        opened={prevClaimDrawerOpen}
        onClose={() => setPrevClaimDrawerOpen(false)}
        onSelect={async (claim) => {
          // if (!(await checkCard("start a claim"))) return;
          setPrefillClaim(claim);
          setClaimDrawerOpen(true);
        }}
      />
    </>
  );
}

export default ClaimToolbar;
